import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faMapLocationDot,
  faHelmetSafety,
  faComments,
  faTemperatureLow,
  faRoad,
  faTruckMoving,
  faCubes,
  faHouseChimney,
  faLeaf,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import Header from '../components/Header';
import CTAButton from '../components/CTAButton';
import EmailAndPhoneButton from '../components/EmailAndPhoneButton';
import SEOHead from '../components/SEOHead';
import Kontaktformular from '../components/Kontaktformular';
import heroImage from '../images/JBC-Entreprenoeren-Jim.jpg';
import jimBillede from '../images/jim-jbc.webp';
import kontaktBaggrund from '../images/jbcBillede2.jpg';
import './styles/OmOs.css';

const fakta = [
  { tal: '2018', tekst: 'Grundlagt' },
  { tal: 'Undløse', tekst: 'Base ved Tølløse' },
  { tal: 'Sjælland', tekst: 'Arbejdsområde' },
];

const vaerdier = [
  {
    icon: faMapLocationDot,
    titel: 'Lokalt forankret',
    tekst: 'Vi har rødderne i Holbæk Kommune og kender området – fra jordbunden til kommunens krav. Det giver færre overraskelser undervejs.',
  },
  {
    icon: faHelmetSafety,
    titel: 'Ordentligt håndværk',
    tekst: 'Vi har bygget vores navn på kvalitet og tillid. Arbejdet skal være udført ordentligt – uanset om opgaven er stor eller lille.',
  },
  {
    icon: faComments,
    titel: 'Tæt dialog',
    tekst: 'Vi sætter pris på den direkte kontakt med vores kunder. Du ved, hvad der sker, og hvem du skal ringe til.',
  },
];

const tjenester = [
  { titel: 'Jordvarme', tekst: 'Energieffektiv opvarmning', path: '/jordvarme', icon: faTemperatureLow },
  { titel: 'Belægning', tekst: 'Fliser, beton og asfalt', path: '/belægning', icon: faRoad },
  { titel: 'Fundamenter', tekst: 'Solide fundamenter til byggeri', path: '/fundamenter', icon: faCubes },
  { titel: 'Tilbygning', tekst: 'Udgravning og fundament', path: '/tilbygning', icon: faHouseChimney },
  { titel: 'Midlertidig vej', tekst: 'Vej- og adgangsanlæg', path: '/midlertidig-vej', icon: faTruckMoving },
  { titel: 'LCA & Bæredygtighed', tekst: 'Klimakrav på byggepladsen', path: '/lca', icon: faLeaf },
];

function OmOs() {
  const navigate = useNavigate();

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'Om JBC Entreprenøren',
    url: 'https://jbcentreprenoeren.dk/om-os',
    mainEntity: { '@id': 'https://jbcentreprenoeren.dk/#business' },
  };

  return (
    <div className="omos">
      <SEOHead
        title="Om os – lokal entreprenør siden 2018 | JBC Entreprenøren"
        description="JBC Entreprenøren er et lokalt entreprenørfirma fra Undløse ved Tølløse, grundlagt i 2018. Vi løser anlægsopgaver for private og erhverv på hele Sjælland."
        url="/om-os"
        structuredData={structuredData}
        image="https://jbcentreprenoeren.dk/images/og-om-os.jpg"
        breadcrumbs={[{ name: 'Hjem', url: '/' }, { name: 'Om os', url: '/om-os' }]}
      />
      <Header />
      <EmailAndPhoneButton />

      <section className="omos-hero">
        <div className="omos-hero-bg">
          <img src={heroImage} alt="" />
        </div>
        <div className="omos-hero-inner">
          <span className="hero-eyebrow">Om os</span>
          <h1>Vi er JBC Entreprenøren</h1>
          <p>
            Et lokalt entreprenørfirma fra Undløse ved Tølløse. Grundlagt i 2018 – og i dag på opgaver over hele Sjælland.
          </p>
          <div className="omos-hero-actions">
            <CTAButton text="Få et gratis tilbud" onClick={() => navigate('/kontakt')} />
            <a href="tel:+4522780476" className="hero-phone-link">
              <span className="hero-phone-icon">
                <FontAwesomeIcon icon={faPhone} />
              </span>
              <span>
                <small>Ring til os</small>
                22 78 04 76
              </span>
            </a>
          </div>
        </div>
      </section>

      <section className="omos-historie" aria-labelledby="historie-titel">
        <div className="omos-historie-inner">
          <div className="omos-historie-tekst">
            <span className="hero-eyebrow">Vores historie</span>
            <h2 id="historie-titel">Bygget på en enkel idé</h2>
            <p>
              JBC Entreprenøren blev grundlagt med ét mål: at levere solidt og pålideligt entreprenørarbejde med rødder
              i det nordvestsjællandske lokalsamfund.
            </p>
            <p>
              Fra vores base i Holbæk Kommune har vi opbygget et ry for kvalitet og tillid hos private husejere og lokale
              virksomheder. Erfaringerne herfra har gjort det muligt for os at vokse – og i dag løser vi opgaver over hele
              Sjælland med samme dedikation, uanset opgavens størrelse.
            </p>
          </div>
          <figure className="omos-portraet">
            <img src={jimBillede} alt="Jim, grundlægger og direktør af JBC Entreprenøren, i en minigraver" loading="lazy" decoding="async" />
            <figcaption>
              <strong>Jim</strong>
              <span>Grundlægger og direktør</span>
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="omos-fakta" aria-label="Fakta om JBC Entreprenøren">
        <ul className="omos-fakta-liste">
          {fakta.map((f) => (
            <li key={f.tal}>
              <strong>{f.tal}</strong>
              <span>{f.tekst}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="omos-vaerdier" aria-labelledby="vaerdier-titel">
        <div className="sektion-intro">
          <span className="hero-eyebrow">Det står vi for</span>
          <h2 id="vaerdier-titel">Tre ting, du kan regne med</h2>
        </div>
        <ul className="omos-vaerdier-liste">
          {vaerdier.map((v) => (
            <li key={v.titel}>
              <span className="omos-ikon" aria-hidden="true">
                <FontAwesomeIcon icon={v.icon} />
              </span>
              <h3>{v.titel}</h3>
              <p>{v.tekst}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="omos-tjenester" aria-labelledby="omos-tjenester-titel">
        <div className="sektion-intro">
          <span className="hero-eyebrow">Det kan vi hjælpe med</span>
          <h2 id="omos-tjenester-titel">Vores tjenester</h2>
        </div>
        <ul className="omos-tjenester-liste">
          {tjenester.map((t) => (
            <li key={t.path}>
              <Link to={t.path} className="omos-tjeneste">
                <span className="omos-tjeneste-ikon" aria-hidden="true">
                  <FontAwesomeIcon icon={t.icon} />
                </span>
                <span className="omos-tjeneste-tekst">
                  <strong>{t.titel}</strong>
                  <span>{t.tekst}</span>
                </span>
                <FontAwesomeIcon icon={faArrowRight} className="omos-tjeneste-pil" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <div className="kontakt-section" style={{ backgroundImage: `url(${kontaktBaggrund})` }}>
        <div className="kontakt-inner">
          <div className="kontakt-content">
            <h2>Lad os tage en snak om dit projekt</h2>
            <p>Ring, skriv eller udfyld formularen – så vender vi tilbage hurtigst muligt.</p>
            <div className="kontakt-info">
              <p><strong>JBC Entreprenøren ApS</strong></p>
              <p>Naurvej 6, Undløse</p>
              <p>4340 Tølløse</p>
              <p>CVR nr. 44384345</p>
              <p>Tlf: <a href="tel:+4522780476">22 78 04 76</a></p>
              <p>Mail: <a href="mailto:jbc@jbcentreprenoeren.dk">jbc@jbcentreprenoeren.dk</a></p>
            </div>
          </div>
          <div className="kontakt-formular">
            <Kontaktformular />
          </div>
        </div>
      </div>
    </div>
  );
}

export default OmOs;
